import { z } from 'zod';
import { uuidSchema } from '../common';
import type { AiMode } from '../../constants';
import {
  kanbanStatusesArraySchema,
  lanesArraySchema,
  validateLaneReferences,
  type KanbanLintWarning,
} from './kanban';
import { primaryRegionSchema, type PrimaryRegion, type PlacementSource } from './organizations';
import {
  hubDisplayNamesField,
  hubStarterQuestionsField,
  hubWelcomeMessagesField,
  type HubPresentationColumns,
} from '../hub-presentation';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

// Param schemas
/**
 * Why `previous_conversations_count` would produce no summaries on this hub.
 *
 * The setting works without an evaluator — ids and `ended_at` come from the index —
 * so the degradation is a block of bare ids rather than an error, which is invisible
 * from the agent's settings. The reasons are ordered by what an operator fixes first;
 * the check returns the first unmet one.
 */
export type PreviousConversationsReadinessError =
  | 'no_conversation_evaluator'
  | 'evaluator_disabled'
  | 'evaluator_no_connection'
  | 'evaluator_summary_not_emitted'
  | 'no_system_channel';

export interface PreviousConversationsReadinessResponse {
  data: { ok: true } | { ok: false; error: PreviousConversationsReadinessError };
}

export const hubIdParam = z.object({ id: uuidSchema });
export const hubAdminIdParam = z.object({ id: uuidSchema, adminId: uuidSchema });
export const hubUserIdParam = z.object({ id: uuidSchema, hubUserId: uuidSchema });
export const hubIdentityIdParam = z.object({ id: uuidSchema, identityId: uuidSchema });
export const hubTeamIdParam = z.object({ id: uuidSchema, teamId: uuidSchema });
export const hubTeamUserDeleteParam = z.object({ id: uuidSchema, teamUserId: uuidSchema });
// Query schemas
export const hubSchemaQuery = z.object({ hub_id: uuidSchema });

/** The identities one page of `GET /:id/users` carries when the request names no `page_size`. */
export const HUB_USERS_PAGE_SIZE_DEFAULT = 100;
/** The most identities one page of `GET /:id/users` carries. */
export const HUB_USERS_PAGE_SIZE_MAX = 200;
/** The longest `search` a page of `GET /:id/users` takes. */
export const HUB_USERS_SEARCH_MAX = 200;

/**
 * The most user ids one HubDO end-user display-name read takes (`listHubUserDisplayNames`); a
 * list page naming more asks in chunks of this size.
 */
export const HUB_USER_NAMES_MAX_IDS = 500;

/** The characters of an end-user display name the HubDO's list reads answer; a longer one is cut. */
export const HUB_USER_DISPLAY_NAME_CHARS = 256;

/** The channels the Users tab narrows its end users to. */
export const HUB_USERS_CHANNEL_TYPES = ['app', 'whatsapp', 'instagram', 'telegram'] as const;
export type HubUsersChannelType = (typeof HUB_USERS_CHANNEL_TYPES)[number];

/**
 * `GET /:id/users`' paging (Settings' Users tab): a page of the hub's end-user identities, newest
 * first, narrowed to one channel and to values containing `search`. Every field is optional, so a
 * request naming none reads the first page. A `cursor` is the `next_cursor` a page answered: the
 * position of its last identity, a positive integer.
 */
export const getHubUsersQuery = z.object({
  cursor: z.string().regex(/^[1-9][0-9]{0,14}$/, 'Invalid cursor').optional(),
  page_size: z.coerce.number().int().min(1).max(HUB_USERS_PAGE_SIZE_MAX).optional(),
  channel_type: z.enum(HUB_USERS_CHANNEL_TYPES).optional(),
  search: z.string().max(HUB_USERS_SEARCH_MAX).optional(),
});

/**
 * Hub-level cross-field check: a status's `lane_slug` must reference a declared
 * lane, and the initial status must not belong to a lane. Only fires when BOTH
 * arrays are present in the request body — a partial PATCH (one array only) is
 * re-validated against the merged config in the backend handler. Issues are
 * re-pathed under `kanban_statuses[i].lane_slug` for field-level UI surfacing.
 */
function refineLaneReferences(
  val: { lanes?: unknown; kanban_statuses?: unknown },
  ctx: z.RefinementCtx,
): void {
  const lanes = val.lanes;
  const statuses = val.kanban_statuses;
  if (lanes == null || statuses == null) return;
  for (const issue of validateLaneReferences(lanes as never, statuses as never)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['kanban_statuses', ...issue.path], message: issue.message });
  }
}

// Body schemas

/** Max length of a hub `preview_label`. Single source for the create/replicate
 *  Zod bound, the hub-update PATCH inline check, and the CLI's fast-fail. */
export const PREVIEW_LABEL_MAX_LENGTH = 60;

/**
 * Per-hub retention of an ended conversation's `conversation_index` row (days) —
 * how long the sidebar "Ended" tab + chat-hub continuous history serve it warm
 * (HubDO SQLite) before the cleanup cron reaps it. Absent →
 * `DEFAULT_ENDED_INDEX_RETENTION_DAYS`. Single source for the Zod bound, the
 * HubDO reap default, and the CLI YAML default-elision. The reap enforces the
 * UPPER bound — it re-clamps to `min(this, effective storage retention)` so an
 * index row can never outlive its archive. The lower ordering (≥
 * `conversation_retention_days`, the DO-alive window) is a RECOMMENDATION, not
 * enforced: a shorter window just cold-serves (CH fallback) earlier — no data
 * loss.
 */
export const DEFAULT_ENDED_INDEX_RETENTION_DAYS = 365;
export const MAX_ENDED_INDEX_RETENTION_DAYS = 730;

/**
 * Per-hub retention of an eval SESSION (days) — how long a terminal
 * `eval_session` and everything it owns (`eval_session_run`,
 * `eval_session_result`, and the eval conversations those runs created) stay in
 * HubDO SQLite before the weekly retention cron retires them.
 *
 * There is deliberately NO default constant here: absent (`null`) means "use
 * `platform_config.default_eval_retention_days`", which is a PlatformDO value
 * and must never be hard-coded (see the effective-value helper,
 * `apps/backend/src/utils/eval-retention.ts`). `0` means retention is DISABLED
 * for the hub — sessions are kept forever. Only the upper bound is fixed here,
 * shared by the Zod bound, the HubDO column CHECK, and the CI clamp.
 *
 * Retiring a session destroys its transcripts: they live only in HubDO. The
 * ClickHouse `is_eval` conversation rows (and their `data.eval_scores`) are NOT
 * a copy of them and deliberately survive retirement.
 */
export const MAX_EVAL_RETENTION_DAYS = 3650;

/**
 * Upper bound of a hub's `end_user_daily_message_limit` (wayai#4948): how many messages one end
 * user may send the hub on the app channel in a 24-hour window. Team sends and senders a grant
 * admits (org members, hub admins) are not counted.
 */
export const MAX_END_USER_DAILY_MESSAGE_LIMIT = 10_000;

/**
 * A hub's `end_user_daily_message_limit`: an integer from 1 to
 * `MAX_END_USER_DAILY_MESSAGE_LIMIT`, or null for no limit. Shared by the hub create and update
 * bodies and the hub-as-code `hub` section.
 */
export const endUserDailyMessageLimitField = z
  .number()
  .int()
  .min(1)
  .max(MAX_END_USER_DAILY_MESSAGE_LIMIT)
  .nullable()
  .optional();

/**
 * Preview label: server-owned, preview-only sibling disambiguator (≤60 chars).
 * Trimmed; empty → null. Shared by hub-create and replicate-preview so the
 * normalization can't drift. The hub-update PATCH enforces the same bound inline
 * (updateHubBody is `.passthrough()`), since `preview_label` reaches it untyped.
 */
export const previewLabelField = z
  .string()
  .max(PREVIEW_LABEL_MAX_LENGTH)
  .optional()
  .nullable()
  .transform((v) => {
    if (v == null) return null;
    const trimmed = v.trim();
    return trimmed || null;
  });

const hubTagsField = z.array(z.string()).nullable().optional();

/**
 * `POST /api/setup/hubs`. Strict (wayai#5035): every key reaches the new hub's row, so a key not
 * declared here is refused rather than stored unchecked or silently dropped. The hub's other
 * settings are set once it exists (`updateHubBody`), where they are checked against it — a
 * `default_hub_team_id`, for one, can name no team of a hub not yet created.
 */
export const createHubBody = z
  .object({
    organization_id: uuidSchema,
    hub_name: z.string().min(1, 'hub_name is required'),
    hub_description: z.string().optional(),
    hub_type: z.enum(['chat', 'task']).optional(),
    kanban_statuses: kanbanStatusesArraySchema.nullable().optional(),
    lanes: lanesArraySchema.nullable().optional(),
    // Optional preview label for the first preview. Server-owned thereafter
    // (change via `wayai relabel` / replicate); the push diff never writes it.
    preview_label: previewLabelField,
    // Write-once at creation; updateHubBody does not accept this.
    primary_region: primaryRegionSchema.optional(),
    // Org tag ids. Ids that name none of the org's tags are dropped; a hub created with
    // any is a tag change (`hubTagChangeRefusalSchema`).
    tags: hubTagsField,
    // Localized end-user presentation (`hub-presentation.ts`), as `updateHubBody` takes it.
    display_names: hubDisplayNamesField,
    welcome_messages: hubWelcomeMessagesField,
    starter_questions: hubStarterQuestionsField,
    end_user_daily_message_limit: endUserDailyMessageLimitField,
  })
  .strict()
  .superRefine(refineLaneReferences);

// Enum fields validated; other fields pass through to the backend updateHub() allowlist.
export const updateHubBody = z
  .object({
    support_model: z.enum(['conversation', 'account']).optional(),
    unassigned_policy: z
      .enum(['default_team', 'auto_assign_random', 'auto_assign_round_robin', 'pull_queue'])
      .optional(),
    default_hub_team_id: uuidSchema.nullable().optional(),
    kanban_statuses: kanbanStatusesArraySchema.nullable().optional(),
    lanes: lanesArraySchema.nullable().optional(),
    // summarization_threshold_tokens relocated to the summarizer AGENT (PR4) — validated/
    // persisted via the agent path (.passthrough() agent body → HubDO column), not here.
    auto_close_inactive_days: z.number().int().min(1).max(180).optional(),
    // Per-hub ConversationDO retention window (days) — keeps an ended conversation's DO
    // alive for post-hoc annotation. Default 7, max 30 — the floor of the retention
    // chain (≤ ended_index_retention_days ≤ effective storage retention).
    conversation_retention_days: z.number().int().min(1).max(30).optional(),
    // Per-hub ended-conversation index retention window (days) — how long the warm
    // "Ended" tab + chat-hub continuous history serve a closed conversation before
    // the cleanup cron reaps its index row. Absent → DEFAULT_ENDED_INDEX_RETENTION_DAYS
    // (365). Re-clamped to the effective storage retention at reap time.
    ended_index_retention_days: z.number().int().min(1).max(MAX_ENDED_INDEX_RETENTION_DAYS).optional(),
    // Per-hub eval-session retention window (days). null CLEARS the override (back to
    // the platform default); 0 disables retention for this hub (keep sessions forever).
    // Nullable — unlike the fields above, "unset" is a meaningful, settable state.
    eval_retention_days: z
      .number()
      .int()
      .min(0)
      .max(MAX_EVAL_RETENTION_DAYS)
      .nullable()
      .optional(),
    // Channel contact access control. Enum-validated explicitly (not via passthrough)
    // so `language`/`access_approval_role` can't reach the DB with an invalid value.
    language: z.enum(['en', 'pt', 'es']).optional(),
    access_request_message: z.string().max(1000).nullable().optional(),
    access_approval_role: z.enum(['admin', 'team']).optional(),
    // AI-failure escalation. When an LLM provider outage persists past this many
    // minutes the conversation escalates to the team; null → code default. The two
    // message fields override the localized (hub.language) delay / hand-off notices;
    // null → localized default. Bounds validated here so out-of-range never persists.
    ai_failure_escalation_minutes: z.number().int().min(1).max(60).nullable().optional(),
    ai_delay_message: z.string().max(1000).nullable().optional(),
    ai_handoff_message: z.string().max(1000).nullable().optional(),
    // Native-completion fallback connection for a degraded harness turn (PR 2.10b).
    // A NATIVE LLM (Agent) connection id on this hub; validated at set-time on the REST
    // PATCH (setup/hubs.ts rejects a non-existent / non-Agent / harness id) and re-checked
    // at fallback time in the turn seam (a stale/missing id simply no-ops). Nullable to clear it.
    fallback_connection_id: uuidSchema.nullable().optional(),
    // Rekor API connection used to reset/clear eval seed fixtures (gh #3090). A
    // connection id on this hub (a REST API (custom tool) connection whose base_url
    // is the Rekor API origin + a bearer/api_key `write:seeds` token). Validated at
    // set-time on the REST PATCH (setup/hubs.ts). Nullable to clear it.
    seed_connection_id: uuidSchema.nullable().optional(),
    // Org tag ids, replacing the hub's; null clears them. A change is refused to anyone
    // but an org admin (`hubTagChangeRefusalSchema`).
    tags: hubTagsField,
    // Localized end-user presentation (`hub-presentation.ts`): keys are supported locales only,
    // each map replaces the stored one, and null or an empty map clears it.
    display_names: hubDisplayNamesField,
    welcome_messages: hubWelcomeMessagesField,
    starter_questions: hubStarterQuestionsField,
    // Messages one end user may send the hub on the app channel per 24 hours; null removes it.
    end_user_daily_message_limit: endUserDailyMessageLimitField,
  })
  .passthrough()
  .superRefine(refineLaneReferences);

/**
 * `details` of the 403 a write answers when it would change a hub's tags and the caller is
 * not an org admin (#4931 owner decision 2): a hub's tags decide which of its org's
 * tag-scoped assets it reaches, so only an org admin changes them. `tags_added` and
 * `tags_removed` name the org tags the write would add and remove. `operation` is `update`
 * for a write to the hub itself, `publish` for a sync carrying a preview's tags to its
 * production hub.
 */
export const HUB_TAG_CHANGE_REFUSED = 'hub_tag_change_requires_org_admin';
export const hubTagChangeRefusalSchema = z.object({
  reason: z.literal(HUB_TAG_CHANGE_REFUSED),
  operation: z.enum(['update', 'publish']),
  hub_id: z.string(),
  tags_added: z.array(z.string()),
  tags_removed: z.array(z.string()),
});
export type HubTagChangeRefusal = z.infer<typeof hubTagChangeRefusalSchema>;

export const addHubAdminBody = z.object({
  user_email: z.string().email('valid email is required'),
});

export const addHubIdentityAuthorizationBody = z.object({
  identity_value: z.string().min(1),
  identity_type: z.string().min(1),
  channel_type: z.string().min(1),
});

// Channel contact access control: approve / block / unblock a contact. The row is
// located by `contact_access_id` OR the (identity_type, identity_value, channel_type)
// tuple; block/unblock may also carry the conversation's user_id so open conversations
// can be transitioned even before a contact_access row exists (block on `everyone`).
export const contactDecisionBody = z.object({
  contact_access_id: uuidSchema.optional(),
  identity_type: z.string().min(1).optional(),
  identity_value: z.string().min(1).optional(),
  channel_type: z.string().min(1).optional(),
  channel_id: uuidSchema.optional(),
  connection_id: uuidSchema.optional(),
  user_id: uuidSchema.optional(),
  sender_display_name: z.string().max(200).optional(),
});

export const approveContactBody = contactDecisionBody.extend({
  user_name: z.string().min(1).max(120).optional(),
});

/** The contacts one page of `GET /api/hubs/:id/contacts` carries when the request names no `page_size`. */
export const CONTACT_ACCESS_PAGE_SIZE_DEFAULT = 100;
/** The most contacts one page of `GET /api/hubs/:id/contacts` carries. */
export const CONTACT_ACCESS_PAGE_SIZE_MAX = 200;
/** The characters of a contact's last message the HubDO keeps and answers; a longer one is cut. */
export const CONTACT_ACCESS_PREVIEW_CHARS = 500;

/**
 * `GET /api/hubs/:id/contacts`' paging: a page of the hub's pending and blocked contacts, most
 * recently seen first. Every field is optional, so a request naming none reads the first page of
 * both statuses. A `cursor` is the `next_cursor` a page answered, passed back unchanged.
 */
export const listContactAccessQuery = z.object({
  status: z.enum(['pending', 'blocked']).optional(),
  cursor: z.string().min(1).max(256).optional(),
  page_size: z.coerce.number().int().min(1).max(CONTACT_ACCESS_PAGE_SIZE_MAX).optional(),
});

// The publicly mutable `hub_team` fields. Both are `.strict()` so an unsupported
// key is rejected at request validation with a contract-level error, rather than
// reaching HubDO's write allowlist and surfacing as an internal RPC failure. The
// DO allowlist (`hub-team-write-fields.ts`) remains defense in depth for internal
// callers that do not pass through these schemas.

// The POST route reads only `team_name`; it does not forward anything else.
export const createHubTeamBody = z.object({
  team_name: z.string().min(1, 'team_name is required'),
}).strict();

// The PATCH route forwards this whole body to `updateHubTeam`, so these must stay
// in step with `HUB_TEAM_UPDATE_FIELDS`. Only `team_name` has a first-party sender
// today (the web app); the other three lost theirs when the MCP setup tools were
// removed. They stay accepted — narrowing the body is a behavioural change to make
// deliberately, with the hub-config sync path checked first, not a side effect.
//
// `team_access_rule` is deliberately an unconstrained string here — narrowing it to
// an enum is a separate behavioural change, not part of closing the unknown-key gap.
export const updateHubTeamBody = z.object({
  team_name: z.string().min(1).optional(),
  conversation_display_only_if_claimed: z.boolean().optional(),
  team_access_rule: z.string().optional(),
  initial_team: z.boolean().optional(),
}).strict();

export const addHubTeamUserBody = z.object({
  user_email: z.string().email('valid email is required'),
});

/** Account-mode: reassign a hub_user to a different team (or clear via null). */
export const reassignHubUserBody = z.object({
  hub_team_id: uuidSchema.nullable(),
});

export const replicatePreviewBody = z.object({
  preview_label: previewLabelField,
});

// =============================================================================
// INFERRED TYPES (for consumers that need request types without importing zod)
// =============================================================================

export type ContactDecisionInput = z.infer<typeof contactDecisionBody>;
export type ApproveContactInput = z.infer<typeof approveContactBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

// --- Hub entity responses ---

/** Resolves a hub_id to its owning organization (used by direct-link redirect). */
export interface GetHubOrganizationResponse {
  data: {
    hub_id: string;
    organization_id: string;
  };
}

// --- Channel contact access control responses ---

/** approve / block / unblock result (HubDO ContactTransitionResult, success branch). */
export interface ContactTransitionResult {
  ok: true;
  user_id: string | null;
  conversation_ids: string[];
  next_access_status: 'approved' | 'pending' | 'blocked';
  hub_user_id?: string;
  identity_type?: string;
  identity_value?: string;
  channel_type?: string;
}

export interface ContactDecisionResponse {
  data: ContactTransitionResult;
}

/** A contact_access row (pending / blocked) for the admin review surface. */
export interface ContactAccessRowResponse {
  contact_access_id: string;
  channel_id: string | null;
  connection_id: string | null;
  identity_type: string;
  identity_value: string;
  channel_type: string;
  user_id: string | null;
  status: 'pending' | 'blocked';
  sender_display_name: string | null;
  /** At most `CONTACT_ACCESS_PREVIEW_CHARS` characters. */
  last_message_preview: string | null;
  notified_at: string | null;
  requested_at: string;
  last_seen_at: string;
  decided_at: string | null;
  decided_by_user_id: string | null;
}

export interface ListContactAccessResponse {
  data: {
    contacts: ContactAccessRowResponse[];
    /** The `cursor` of the next page, or null after the last. */
    next_cursor: string | null;
  };
}

/**
 * Full hub row as returned by get hub by ID — with the localized end-user presentation
 * (`HubPresentationColumns`, `hub-presentation.ts`): each map, or null when the hub sets none.
 */
export interface HubResponse extends HubPresentationColumns {
  hub_id: string;
  created_at: string;
  updated_at: string;
  hub_name: string;
  hub_type: string;
  hub_description: string | null;
  ai_mode: AiMode;
  non_app_permission: string;
  created_by: string;
  hub_contexts: string[] | null;
  hub_course_name: string | null;
  template_id: string;
  kanban_statuses: Record<string, unknown>[] | null;
  /** Optional presentational lane definitions for board grouping (see kanban.ts laneSchema). */
  lanes: Record<string, unknown>[] | null;
  timezone: string | null;
  hub_environment: string;
  production_hub_id: string | null;
  branch_name: string | null;
  last_ci_activity_at: string | null;
  ci_commit_sha: string | null;
  preview_label: string | null;
  support_model: 'conversation' | 'account';
  unassigned_policy: 'default_team' | 'auto_assign_random' | 'auto_assign_round_robin' | 'pull_queue';
  default_hub_team_id: string | null;
  storage_retention_months: number | null;
  // flag_conditions relocated to the conversation_evaluator AGENT (AgentResponse.flag_conditions).
  // summarization_threshold_tokens relocated to the summarizer AGENT (AgentResponse.summarization_threshold_tokens).
  // monitor_config relocated to the monitor AGENT (AgentResponse.monitor_config).
  auto_close_inactive_days: number;
  conversation_retention_days: number;
  /** Days an ended conversation's index row is retained warm; null → the default (365). */
  ended_index_retention_days: number | null;
  /** Days a terminal eval session is retained; null → the platform default, 0 → disabled. */
  eval_retention_days: number | null;
  /** Channel contact access control (hub-scoped). */
  language: 'en' | 'pt' | 'es';
  access_request_message: string | null;
  access_approval_role: 'admin' | 'team';
  /** AI-failure escalation: budget-to-team (null → code default) + localized notice overrides (null → localized default). */
  ai_failure_escalation_minutes: number | null;
  ai_delay_message: string | null;
  ai_handoff_message: string | null;
  /** Messages one end user may send the hub on the app channel per 24 hours; null → no limit. */
  end_user_daily_message_limit: number | null;
  tags: string[];
  holidays: string[] | null;
  org_id: string;
  primary_region: PrimaryRegion | null;
  placement_region: PrimaryRegion | null;
  placement_source: PlacementSource | null;
}

// --- Hub admin user ---

export interface HubAdminUserResponse {
  hub_admin_user_id: string;
  hub_id: string;
  created_at: string;
  user_id: string | null;
  enabled: boolean;
  user_email: string;
}

// --- Hub user ---

export interface HubUserResponse {
  hub_user_id: string;
  hub_id: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  enabled: boolean;
  /** Account-mode: the team that owns this end user ("the account"). */
  hub_team_id: string | null;
  /** 'grant' = pre-authorized access row. 'auto' = created on inbound in account-mode hubs (no platform access). */
  source: 'grant' | 'auto';
}

/** Hub user with linked identities (as returned by fetchHubById). */
export interface HubUserWithIdentitiesResponse extends HubUserResponse {
  identities: HubUserLinkedIdentityResponse[];
}

export interface HubUserLinkedIdentityResponse {
  hub_user_identity_id: string;
  identity_value: string;
  identity_type: string;
  channel_type: string;
  user_identity_id: string | null;
}

// --- Hub user identity (pre-authorized) ---

export interface HubUserIdentityResponse {
  hub_user_identity_id: string;
  hub_user_id: string | null;
  user_identity_id: string | null;
  identity_value: string;
  identity_type: string;
  channel_type: string;
  created_at: string;
  updated_at: string;
  hub_id: string;
}

// --- Hub team ---

export interface HubTeamResponse {
  hub_team_id: string;
  created_at: string;
  team_name: string;
  hub_id: string;
  conversation_display_only_if_claimed: boolean;
  team_access_rule: string;
  initial_team: boolean | null;
}

/** Team with nested team users (as returned by fetchHubById). */
export interface HubTeamWithUsersResponse extends HubTeamResponse {
  team_users: HubTeamUserResponse[];
}

// --- Hub team user ---

export interface HubTeamUserResponse {
  hub_team_user_id: string;
  hub_team_id: string;
  created_at: string;
  user_id: string | null;
  enabled: boolean;
  user_email: string;
}

// --- Composite hub responses ---

/** GET /:id/users — hub with admin users, hub users, teams, pre-authorized identities. */
export interface GetHubUsersResponse {
  data: {
    hub: HubResponse & {
      /**
       * The voice-calls AUTHORING signal: true only when the platform voice-calls switch
       * is on (`voiceCallsEnabled`).
       * It drives the screens that author a call setup — the `pilot_voice` agent editor and
       * the steering editor — which read it from Settings' hub store, the one this endpoint
       * loads. The Realtime connector needs no client signal: `GET /setup/connections`
       * offers it only while the same gate is open. It needs no `pilot_voice`
       * agent or Realtime connection, because those screens are how a hub gets its first ones.
       * Server-computed on every read, never stored; a UI hint, not an authorization —
       * every gated write re-checks the gate itself.
       */
      voice_calls_authoring_enabled: boolean;
    };
    admin_users: HubAdminUserResponse[];
    /**
     * One page of end-user identities (`getHubUsersQuery`), split as the Users tab lists them:
     * the grants the page's linked identities belong to, each with ONLY its identities on this
     * page (a later page may add more to the same grant), and the page's pre-authorized ones.
     */
    users: HubUserWithIdentitiesResponse[];
    pre_authorized_identities: HubUserIdentityResponse[];
    /** The `cursor` of the next page, or null after the last. */
    next_cursor: string | null;
    teams: HubTeamWithUsersResponse[];
  };
}

/** POST / — create hub returns array with single hub plus optional kanban warnings. */
export interface CreateHubResponse {
  data: HubResponse[];
  /** Non-blocking lint warnings (e.g. unreachable kanban statuses). Empty when no warnings apply. */
  warnings?: KanbanLintWarning[];
}

/** PATCH /:id — update hub returns array with single hub plus optional kanban warnings. */
export interface UpdateHubResponse {
  data: HubResponse[];
  warnings?: KanbanLintWarning[];
}

// --- Hub admin user operations ---

export interface AddHubAdminResponse {
  data: HubAdminUserResponse[];
}

// --- Hub user identity operations ---

export interface AddHubIdentityAuthorizationResponse {
  data: HubUserIdentityResponse[];
}

// --- Hub team operations ---

export interface CreateHubTeamResponse {
  data: HubTeamResponse[];
}

export interface UpdateHubTeamResponse {
  data: HubTeamResponse[];
}

// --- Hub team user operations ---

export interface AddHubTeamUserResponse {
  data: HubTeamUserResponse[];
}

// --- Hub branching responses ---

/** GET /:id/diff */
export interface DiffEntry {
  name: string;
  type?: string;
  status: 'added' | 'removed' | 'updated';
}

export interface HubDiffResult {
  has_changes: boolean;
  is_first_publish: boolean;
  hub_changed: boolean;
  agents: DiffEntry[];
  connections: DiffEntry[];
  tools: DiffEntry[];
  channels: DiffEntry[];
  states: DiffEntry[];
  resources: DiffEntry[];
  /** A connection's message templates, which publish and sync copy too. Absent from a server older than wayai#4931 PR 4a. */
  message_templates?: DiffEntry[];
  // Every other type publish and sync copy. Each is absent from a server older than wayai#4931 PR 1d.
  /** An agent's parameters; `type` names their agent. */
  agent_parameters?: DiffEntry[];
  mcp_tools?: DiffEntry[];
  teams?: DiffEntry[];
  /** `type` names the resource a folder belongs to. */
  resource_folders?: DiffEntry[];
  /** `type` names the resource a file belongs to. */
  resource_files?: DiffEntry[];
  /** An agent's link to a resource: `name` is the resource, `type` the agent. */
  agent_resources?: DiffEntry[];
  evals?: DiffEntry[];
  /** The hub's own contact lists (wayai#4931 PR 2c). */
  contact_lists?: DiffEntry[];
  automations?: DiffEntry[];
}

/** The `HubDiffResult` keys that hold a list of `DiffEntry`, one per entity type. */
export type HubDiffSection = {
  [K in keyof HubDiffResult]-?: NonNullable<HubDiffResult[K]> extends DiffEntry[] ? K : never;
}[keyof HubDiffResult];

/** Every section, once, in the order a client lists them: a section missing here fails to compile. */
const HUB_DIFF_SECTION_SET = {
  agents: true, agent_parameters: true, connections: true, mcp_tools: true, tools: true, channels: true,
  states: true, teams: true, resources: true, resource_folders: true, resource_files: true,
  agent_resources: true, message_templates: true, contact_lists: true, automations: true, evals: true,
} as const satisfies Record<HubDiffSection, true>;

/**
 * The sections of a `HubDiffResult`, in the order `wayai publish` and the web publish modal list
 * them. Both walk this list, so neither can omit a section the other shows. A client reads a
 * section absent from an older server as empty.
 */
export const HUB_DIFF_SECTIONS = Object.keys(HUB_DIFF_SECTION_SET) as ReadonlyArray<HubDiffSection>;

export interface GetHubDiffResponse {
  data: HubDiffResult;
}

/** POST /:id/publish */
export interface PublishHubResponse {
  data: {
    production_hub_id: string;
  };
}

/** POST /:id/sync */
export interface SyncHubResponse {
  data: {
    synced: boolean;
  };
}

/** POST /:id/replicate-preview */
export interface ReplicatePreviewHubResponse {
  data: {
    preview_hub_id: string;
  };
}

// --- Hub schema response ---

export interface HubSchemaAgentToolResponse {
  tool_id: string;
  tool_name: string;
  tool_description: string | null;
  is_native: boolean;
  enabled: boolean;
  tool_group: string | null;
  tool_native_id: string | null;
  connector_id: string | null;
  connector_name: string | null;
}

export interface HubSchemaAgentResponse {
  agent_id: string;
  agent_name: string;
  agent_role: string | null;
  agent_settings: unknown;
  instructions: string | null;
  tools: HubSchemaAgentToolResponse[];
}

export interface HubSchemaConnectionResponse {
  connection_id: string;
  connection_display_name: string;
  connector_id: string | null;
  connector_name: string | null;
  connector_type: string;
  connector_description: string | null;
  enabled: boolean;
}

export interface HubSchemaNativeToolInstallationResponse {
  agent_id: string;
  tool_id: string;
  enabled: boolean;
}

export interface HubSchemaNativeToolResponse {
  tool_native_id: string;
  tool_name: string;
  tool_description: string | null;
  is_delegation: boolean;
  installations: HubSchemaNativeToolInstallationResponse[];
}

export interface HubSchemaTeamResponse {
  hub_team_id: string;
  team_name: string;
  team_access_rule: string;
  initial_team: boolean;
  conversation_display_only_if_claimed: boolean;
}

export interface HubSchemaHubResponse {
  hub_id: string;
  hub_name: string;
  hub_description: string | null;
  ai_mode: AiMode | null;
  created_at: string;
}

export interface GetHubSchemaResponse {
  data: {
    hub: HubSchemaHubResponse;
    agents: HubSchemaAgentResponse[];
    connections: HubSchemaConnectionResponse[];
    teams: HubSchemaTeamResponse[];
    native_tools: HubSchemaNativeToolResponse[];
  };
}

// --- Channel test identities ---

// First-party, authMiddleware-gated body: MUST be a contract, never a raw
// `c.req.json<T>()` cast (AGENTS.md "contracts first"). Without it, a malformed
// `test_identities` was silently coerced to [] by the DO-layer normalizer and
// returned 200, masking client bugs instead of a clean 400.
// Bounds are deliberately generous — a preview hub's test contacts number in the
// handful, so these never reject realistic input; they cap the shared `:testers`
// KV map that updateTestersIndex JSON-serializes and the inbound routing path
// re-parses on a warm path (25 MB KV value ceiling). 320 = RFC 5321 max email.
export const updateChannelTestIdentitiesBody = z.object({
  test_identities: z.array(z.string().max(320)).max(1000),
});

// --- Claim codes (preview hub tester registration) ---

export const claimCodeChannelParam = z.object({
  id: uuidSchema,
  channelId: uuidSchema,
});

export const claimCodeDeleteParam = z.object({
  id: uuidSchema,
  channelId: uuidSchema,
  code: z.string().regex(/^[A-Za-z0-9]{6}$/, 'Invalid claim code format').transform((s) => s.toUpperCase()),
});

export interface ClaimCodeSummaryResponse {
  code: string;
  created_at: string;
  created_by: string;
}

export interface GenerateClaimCodeResponse {
  data: {
    code: string;
    expires_at: string;
    channel_type: string;
  };
}

export interface ListClaimCodesResponse {
  data: {
    codes: ClaimCodeSummaryResponse[];
  };
}

// --- User + Hub schema responses (scoped fetches for Settings) ---

interface SchemaEntityItem {
  id: string;
  name: string;
  [key: string]: unknown;
}

/** GET /api/setup/user-schema — orgs + hubs hierarchy, no agents/tools. */
export interface GetUserSchemaResponse {
  data: {
    connectors: unknown[];
    native_tools: unknown[];
    flat: {
      organizations: SchemaEntityItem[];
      hubsByOrg: Record<string, SchemaEntityItem[]>;
    };
    lastUpdated: string;
  };
}

/** GET /api/setup/hub-schema/:hubId — agents + tools + states for a single hub. */
export interface GetHubDetailSchemaResponse {
  data: {
    hub_id: string;
    agents: SchemaEntityItem[];
    toolsByAgent: Record<string, SchemaEntityItem[]>;
    states: SchemaEntityItem[];
  };
}
